import React from "react";
import { DisplayFilterProps, FiltersPlugin, useGuiFilters } from "@me8eon/filters";
import { useTranslation } from "@me8eon/translation";
import { NameAnd } from "@me8eon/record_utils";
import { EonUiDropdown, EonUiDropdownOption } from "@eon-ui/eon-ui-components-react";
import { EonUiDropdownCustomEvent } from "@eon-ui/eon-ui-components";
import { useGlobalAggregations } from "@me8eon/all_data_sources";
import { useSelectedDataView } from "@me8eon/data_view_filter";

export const aggregateFilterName = "agg";


/**
 * Represents the aggregate filter plugin configuration for the FiltersPlugin system.
 *
 * @typeParam NameAnd<T> - A generic type representing a name-value pair where the value is of type `T`.
 * @typeParam string - The type of the filter value, which is a string in this case.
 *
 * @property plugin - Specifies the type of plugin, which is "filter".
 * @property type - The unique name or identifier for the aggregate filter.
 * @property DefaultDisplay - The React component used to render the default display for the aggregate filter.
 */
export const aggregateFilterPlugin: FiltersPlugin<NameAnd<string>, string> = {
    plugin: "filter",
    type: aggregateFilterName,
    changeCausesSubmit: true,
    deleteWhenClean: true,
    DefaultDisplay: AggregateFilterDisplay,
};


/**
 * Component to display an aggregate filter dropdown.
 *
 * @param {DisplayFilterProps<string>} props - The props for the component.
 * @param {Record<string, any>} props.filterOps - The filter operations available for the dropdown.
 * @param {string} props.rootId - The unique identifier for the root element of the dropdown.
 * @param {string} props.value - The current value of the filter.
 *
 * @returns {JSX.Element} A dropdown component for selecting aggregate filter options.
 *
 * @remarks
 * - The component uses `useGuiFilters` to manage the state of GUI filters.
 * - It fetches global aggregations using `useGlobalAggregations`.
 * - The dropdown options are dynamically generated based on the aggregation data.
 * - The `onValueChanged` handler updates the GUI filters and triggers a search.
 * - The `onDropdownOpened` and `onDropdownClosed` handlers manage the dropdown's z-index styling.
 */
export function AggregateFilterDisplay({ filterOps, rootId, filterName, value }: DisplayFilterProps<string>) {

    const [filterValue, setFilterValue] = filterOps;
    const globalAggregates = useGlobalAggregations();
    const agg = filterName && value ? globalAggregates[value] : {};
    const options = Object.entries(agg || {});
    const t = useTranslation();
    const selectedDataView = useSelectedDataView();
    const defaultValue = !filterValue ? selectedDataView.default?.[value ?? ""] ?? null : null;
    const size = selectedDataView.size ?? "normal";
    const conditionToHide = selectedDataView.conditionToHide;
    const [guiFilters] = useGuiFilters();

    if (value && conditionToHide?.[value]?.(guiFilters)) {
        return <></>;
    }

    return (
        <EonUiDropdown
            data-testid={`agg-dropdown-${filterName}`}
            onValueChanged={(e: EonUiDropdownCustomEvent<string>) => {
                setFilterValue(e.target.value);
            }}
            onDropdownOpened={(e: EonUiDropdownCustomEvent<string>) => {
                // e.target.style.zIndex = getTimeInSeconds().toString();
            }}
            onDropdownClosed={(e: EonUiDropdownCustomEvent<string>) => {
                e.target.style.removeProperty("z-index");
            }}
            value={filterValue ?? defaultValue}
            multiselect={true}
            key={filterValue}
            name="time_range"
            size={size}
            label={t(`filterPage.filterMenu.${value}`)}
            showSelectedItems={true}
        >
            {options.map(([item, count], index) => (
                <EonUiDropdownOption key={item} value={item} label={`${item} (${count})`} />
            ))}
        </EonUiDropdown>
    );
}